import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCourses } from '../data/course-data';

const AIAgent = ({ onClose }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [aiMessage, setAiMessage] = useState('');
    const [showAIAgent, setShowAIAgent] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        experience: '',
        interests: [],
        goals: '',
        timeCommitment: '',
        budget: ''
    });

    const [recommendedCourses, setRecommendedCourses] = useState([]);

    useEffect(() => {
        setShowAIAgent(true);
    }, []);

    useEffect(() => {
        if (step === 1) {
            setAiMessage('');
            const message = "Hi! I'm Zuriel, your AI Course Advisor. Let me help you find the perfect course for your career goals.";
            let i = 0;
            const typing = setInterval(() => {
                if (i < message.length) {
                    setAiMessage(prev => prev + message[i]);
                    i++;
                } else {
                    clearInterval(typing);
                }
            }, 50);
            return () => clearInterval(typing);
        }
    }, [step]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleInterestChange = (interest) => {
        setFormData(prev => ({
            ...prev,
            interests: prev.interests.includes(interest)
                ? prev.interests.filter(i => i !== interest)
                : [...prev.interests, interest]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const nameParts = (formData.name || '').split(' ');
        const interestsConcatenated = formData.interests.join(', ') || 'N/A';

        const registrationData = {
            firstname: nameParts[0] || 'N/A',
            lastname: nameParts.slice(1).join(' ') || 'N/A',
            phoneno: formData.phone || 'N/A',
            email: formData.email || 'N/A',
            residence: 'N/A',
            course: interestsConcatenated
        };

        try {
            const response = await fetch('https://server.abiatechhub.com/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(registrationData)
            });

            const result = await response.json();

            if (response.ok) {
                console.log('Registration successful:', result);
            } else {
                console.log('API Error:', result.message || 'Something went wrong.');
            }
        } catch (error) {
            console.log('Fetch Error:', error);
        } finally {
            // Always show recommendations after any submit attempt
            const courses = getCourses();
            const recommendations = recommendCourses(courses, formData);
            setRecommendedCourses(recommendations);
            setStep(3);
            setIsLoading(false);
        }
    };


    const recommendCourses = (courses, data) => {
        return courses.filter(course => {
            const courseName = course.name.toLowerCase();
            const courseDescription = course.description.toLowerCase();

            const matchesInterest = data.interests.some(interest =>
                courseName.includes(interest.toLowerCase()) ||
                courseDescription.includes(interest.toLowerCase())
            );

            const matchesGoals = data.goals.toLowerCase().includes('web') && courseName.includes('web') ||
                                 data.goals.toLowerCase().includes('design') && courseName.includes('design') ||
                                 data.goals.toLowerCase().includes('data') && courseName.includes('data') ||
                                 data.goals.toLowerCase().includes('marketing') && courseName.includes('marketing');

            return matchesInterest || matchesGoals;
        });
    };

    const handleCourseSelect = (courseSlug) => {
        localStorage.setItem('prospectData', JSON.stringify(formData));
        navigate(`/courses/${courseSlug}`);
        onClose();
    };

    const progress = (step / 3) * 100;

    const handleClose = () => {
        setShowAIAgent(false);
        if (onClose) onClose();
    };

    if (!showAIAgent) return null;

    return (
        <div className="ai-agent-popup">
            <div className="ai-agent-content">
                <button className="close-btn" onClick={handleClose} aria-label="Close">
                    <i className="fas fa-times"></i>
                </button>
                <div className="ai-header">
                    <h2>Zuriel</h2>
                    <div className="ai-avatar">
                        <i className="fas fa-robot"></i>
                    </div>
                </div>

                <div className="progress-bar">
                    <div className="progress" style={{ width: `${progress}%` }}></div>
                </div>

                {isLoading ? (
                    <div className="loading">
                        <div className="loading-spinner"></div>
                        <p>Analyzing your preferences...</p>
                    </div>
                ) : (
                    <>
                        {step === 1 && (
                            <div className="form-step">
                                <div className="ai-message">
                                    <p>{aiMessage}</p>
                                </div>
                                <form onSubmit={() => setStep(2)}>
                                    <h3>Let's get to know you</h3>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="name"
                                            placeholder="Your Name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="Your Email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="tel"
                                            name="phone"
                                            placeholder="Your Phone Number"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <button type="submit" className="btn btn-primary">
                                        Continue <i className="fas fa-arrow-right"></i>
                                    </button>
                                </form>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="form-step">
                                <div className="ai-message">
                                    <p>Great! Now, let's understand your learning preferences and goals.</p>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label>What's your current experience level?</label>
                                        <select name="experience" value={formData.experience} onChange={handleInputChange} required>
                                            <option value="">Select your experience</option>
                                            <option value="beginner">Beginner</option>
                                            <option value="intermediate">Intermediate</option>
                                            <option value="advanced">Advanced</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>What areas interest you? (Select multiple)</label>
                                        <div className="interests-grid">
                                            {['Web Development', 'Design', 'Data Analysis', 'Marketing', 'Programming', 'Security', 'AI', 'Blockchain'].map(interest => (
                                                <label key={interest} className="interest-checkbox">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.interests.includes(interest)}
                                                        onChange={() => handleInterestChange(interest)}
                                                    />
                                                    {interest}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>What are your career goals?</label>
                                        <textarea
                                            name="goals"
                                            value={formData.goals}
                                            onChange={handleInputChange}
                                            placeholder="Tell us about your career aspirations"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>How much time can you commit per week?</label>
                                        <select name="timeCommitment" value={formData.timeCommitment} onChange={handleInputChange} required>
                                            <option value="">Select time commitment</option>
                                            <option value="5-10">5-10 hours</option>
                                            <option value="10-20">10-20 hours</option>
                                            <option value="20+">20+ hours</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>What's your budget range?</label>
                                        <select name="budget" value={formData.budget} onChange={handleInputChange} required>
                                            <option value="">Select budget range</option>
                                            <option value="50k">Under ₦50,000</option>
                                            <option value="100k">₦50,000 - ₦100,000</option>
                                            <option value="200k">₦100,000 - ₦200,000</option>
                                            <option value="300k">₦200,000+</option>
                                        </select>
                                    </div>

                                    <button type="submit" className="btn btn-primary">
                                        Get Recommendations <i className="fas fa-robot"></i>
                                    </button>
                                </form>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="form-step">
                                <div className="ai-message">
                                    <p>Based on your preferences, I've analyzed our course catalog and found these perfect matches for you:</p>
                                </div>
                                <div className="recommendations">
                                    <div className="recommended-courses">
                                        {recommendedCourses.map(course => (
                                            <div key={course.slug} className="recommended-course">
                                                <img src={course.img} alt={course.alt} />
                                                <h4>{course.name}</h4>
                                                <p>{course.description}</p>
                                                <button
                                                    onClick={() => handleCourseSelect(course.slug)}
                                                    className="btn btn-primary"
                                                >
                                                    View Course <i className="fas fa-arrow-right"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AIAgent;